/* eslint-disable prefer-const */
/* eslint-disable no-nested-ternary */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { message } from 'antd-v5';
import { useFlags } from 'launchdarkly-react-client-sdk';
import ProductsV2List from '../List';
import ProductsV2Details from '../Details';
import RolexProductDetails from '../RolexDetails';
import usePreviousValue from '../../../../../hooks/usePreviousValue';
import { getVendorById, getVendorItemById, searchVendorItems } from '../../../../../api/vendors';
import { parseClientbookProduct, parseVendorProduct } from '../../helpers';
import { getProductById, getProductsElasticSearch } from '../../../../../api/products';
import { updateVendorItemViewDate } from '../../../../Navigation_V2/Edison/apiRequests';
import ProductsV2Drawer from '../Drawer';
import ClientCollectionModal from '../../../ProductDetails/Layout/components/ClientCollectionModal';
import SendProductModal from '../../../ProductDetails/Layout/components/SendProductModal';
import DeleteProductModal from '../../../../../componentsV2/Products/DeleteProductModal';
import AddRolexInquiryModal from '../../../../../componentsV2/Rolex/AddRolexInquiryModal';

const GET_PRODUCTS_LIMIT = 20;
const INITIAL_VENDOR_FILTERS = { collections: [], families: [], materials: [] };

const ProductContainer = ({
  vendors,
  selectedFilter,
  selectedAddProduct,
  listStyle,
  isClientPage,
  selectedProducts,
  toggleProductSelection,
  selectedAndDisabledProducts,
  ...props
}) => {
  const flags = useFlags();
  const searchParams = useMemo(() => new URLSearchParams(window.location.search), []);
  const productIdParam = useMemo(() => Number(searchParams.get('productId')), [searchParams]);

  const [showBrandDropdown, setShowBrandDropdown] = useState(false);
  const [productsLoading, setProductsLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [productsPage, setProductsPage] = useState(1);
  const [productsTotal, setProductsTotal] = useState(0);
  const [productsSearch, setProductsSearch] = useState('');
  const [searchField, setSearchField] = useState(localStorage.getItem('productsV2SearchField') || 'name');
  const [selectedProductId, setSelectedProductId] = useState(productIdParam || null);

  const [productLoading, setProductLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedImageUrl, setSelectedImageUrl] = useState(null);

  const [showClientCollectionModal, setShowClientCollectionModal] = useState(false);
  const [showShareProductModal, setShowShareProductModal] = useState(false);
  const [showDeleteProductModal, setShowDeleteProductModal] = useState(false);
  const [showDetailsDrawer, setShowDetailsDrawer] = useState(false);

  const [selectedBrands, setSelectedBrands] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [showAddInquiryModal, setShowAddInquiryModal] = useState(false);
  const [inquiryRefreshKey, setInquiryRefreshKey] = useState(0);
  const [vendorDetails, setVendorDetails] = useState(null);
  const [vendorFilters, setVendorFilters] = useState(INITIAL_VENDOR_FILTERS);

  const prevProductsPage = usePreviousValue(productsPage);

  const inquiryInitialModels = useMemo(
    () => (selectedProduct?.mpn ? [selectedProduct.mpn] : []),
    [selectedProduct?.mpn],
  );

  const isVendorSelected = useMemo(() => {
    return !['allProducts', /* 'outOfStockItems', */ 'inStockProducts'].includes(selectedFilter);
  }, [selectedFilter]);

  const selectedVendor = useMemo(() => {
    if (!isVendorSelected) return null;
    return vendors.find(vendor => vendor?.id?.toString() === selectedFilter?.toString()) || null;
  }, [vendors, selectedFilter, isVendorSelected]);

  const isVendorRolex = useMemo(() => {
    return selectedVendor?.key === 'rolex';
  }, [selectedVendor]);

  useEffect(() => {
    if (isVendorSelected && selectedFilter) {
      getVendorById(selectedFilter)
        .then(response => {
          setVendorDetails(response?.data || response);
        })
        .catch(() => setVendorDetails(null));
    } else {
      setVendorDetails(null);
    }
    setVendorFilters(INITIAL_VENDOR_FILTERS);
  }, [selectedFilter, isVendorSelected]);

  const hasVendorFilters = useMemo(() => {
    return vendorDetails?.hasCollection || vendorDetails?.hasFamily || vendorDetails?.hasMaterial;
  }, [vendorDetails]);

  const title = useMemo(() => {
    if (selectedFilter === 'allProducts') {
      return 'All Products';
    }

    if (selectedFilter === 'inStockProducts') {
      return 'In Stock';
    }

    if (!selectedVendor) {
      return '';
    }

    return selectedVendor.imageUrl || selectedVendor.name;
  }, [selectedFilter, selectedVendor]);

  const productsFilters = useMemo(() => {
    const filters = {};

    if (!isVendorSelected) {
      // if (selectedFilter === 'outOfStockItems') {
      //   filters.available = false;
      // }

      if (selectedFilter === 'inStockProducts') {
        filters.available = true;
      }

      if (selectedBrands.length > 0) {
        filters.brand = {
          $in: selectedBrands.map(item => item?.value || item),
        };
      }

      if (selectedCategories.length > 0) {
        filters.category = {
          $in: selectedCategories.map(item => item?.value || item),
        };
      }
    }

    return filters;
  }, [selectedFilter, isVendorSelected, selectedBrands, selectedCategories]);

  const fetchClientbookProducts = useCallback(
    async clear => {
      setProductsLoading(true);

      try {
        const params = {
          page: productsPage,
          limit: GET_PRODUCTS_LIMIT,
          search: productsSearch,
          filters: productsFilters,
          searchField,
        };

        const response = await getProductsElasticSearch({ params });

        const parsedProducts = response.data.data.map(parseClientbookProduct);

        if (clear) {
          setProducts(parsedProducts);
          const keepExisting = selectedProductId && parsedProducts.some(p => p.id === selectedProductId);
          setSelectedProductId(keepExisting ? selectedProductId : parsedProducts[0]?.id || null);
        } else {
          setProducts([...products, ...parsedProducts]);
          setSelectedProductId(selectedProductId || parsedProducts[0]?.id || null);
        }

        setProductsTotal(Number(response.data.total));
        setProductsPage(Number(response.data.page));
      } catch (error) {
        message.error('Error fetching products');
      } finally {
        setProductsLoading(false);
      }
    },
    [productsPage, productsSearch, productsFilters, products, selectedProductId, searchField],
  );

  const fetchVendorItems = useCallback(
    async clear => {
      setProductsLoading(true);

      try {
        const offset = (productsPage - 1) * GET_PRODUCTS_LIMIT;
        const response = await searchVendorItems({
          vendorIds: [Number(selectedFilter)],
          query: productsSearch,
          offset,
          limit: GET_PRODUCTS_LIMIT,
          collections: vendorFilters.collections,
          families: vendorFilters.families,
          materials: vendorFilters.materials,
        });

        const items = response?.data || [];
        const parsedProducts = items.map(parseVendorProduct);

        if (clear) {
          setProducts(parsedProducts);
          const keepExisting = selectedProductId && parsedProducts.some(p => p.id === selectedProductId);
          setSelectedProductId(keepExisting ? selectedProductId : parsedProducts[0]?.id || null);
        } else {
          setProducts([...products, ...parsedProducts]);
          setSelectedProductId(selectedProductId || parsedProducts[0]?.id || null);
        }

        setProductsTotal(Number(response?.total || 0));
        setProductsPage(productsPage);
      } catch (error) {
        message.error('Error fetching products');
      } finally {
        setProductsLoading(false);
      }
    },
    [selectedFilter, productsPage, productsSearch, productsFilters, products, selectedProductId, vendorFilters],
  );

  const fetchProducts = useCallback(
    async (clear = false) => {
      if (isVendorSelected) {
        fetchVendorItems(clear);
      } else {
        fetchClientbookProducts(clear);
      }
    },
    [isVendorSelected, fetchVendorItems, fetchClientbookProducts],
  );

  const trackVendorItemViewed = useCallback(async (vendorId, mpn) => {
    try {
      await updateVendorItemViewDate([{ vendorId, mpn }], 'product_list', 'vendor_item_viewed');
    } catch (error) {
      //
    }
  }, []);

  const fetchVendorItem = useCallback(async () => {
    setProductLoading(true);

    try {
      const response = await getVendorItemById(selectedFilter, selectedProductId);
      const parsedProduct = parseVendorProduct(response.data);

      setSelectedProduct(parsedProduct);
      trackVendorItemViewed(selectedFilter, parsedProduct.mpn);
    } catch (error) {
      message.error('Error fetching product');
    } finally {
      setProductLoading(false);
    }
  }, [selectedFilter, selectedProductId]);

  const fetchClientbookProduct = useCallback(async () => {
    setProductLoading(true);

    try {
      const response = await getProductById(selectedProductId);
      const parsedProduct = parseClientbookProduct(response.data);

      setSelectedProduct(parsedProduct);
    } catch (error) {
      message.error('Error fetching product');
    } finally {
      setProductLoading(false);
    }
  }, [selectedProductId]);

  const fetchProduct = useCallback(async () => {
    if (isVendorSelected) {
      fetchVendorItem();
    } else {
      fetchClientbookProduct();
    }
  }, [isVendorSelected, fetchVendorItem, fetchClientbookProduct]);

  useEffect(() => {
    if (prevProductsPage !== undefined) {
      setProductsPage(1);
      setProductsTotal(0);
      setProducts([]);
      setSelectedProductId(null);

      if (productsPage === 1) {
        fetchProducts(true);
      }
    }
  }, [selectedFilter, productsSearch, searchField, productsFilters, vendorFilters]);

  useEffect(() => {
    fetchProducts();
  }, [productsPage]);

  useEffect(() => {
    setSelectedProduct(null);

    if (selectedProductId) {
      fetchProduct();
    }
  }, [selectedProductId]);

  const handleOnSuccessSave = useCallback(
    ({ id, image, name, ...rest }) => {
      if (!id) return;
      setSelectedProductId(id);
      let findProductIndex = products.findIndex(product => product.id === id);

      if (findProductIndex > -1) {
        let updateProductsList = [...products];
        updateProductsList[findProductIndex] = parseClientbookProduct({
          id,
          image,
          name,
          qty: 1,
          ...rest,
        });
        setProducts(updateProductsList);
      } else {
        let updateProductsList = [
          parseClientbookProduct({
            id,
            image,
            name,
            qty: 1,
          }),
          ...products,
        ];
        setProducts(updateProductsList);
      }
    },
    [products],
  );

  const handleDeleteProduct = useCallback(
    id => {
      setSelectedProductId(null);
      let updateProducts = products.filter(product => product.id !== id);
      setProducts(updateProducts);
    },
    [products],
  );

  useEffect(() => {
    if (selectedAddProduct && selectedAddProduct.id) {
      let findProductIndex = products.findIndex(product => product.id === selectedAddProduct.id);

      if (findProductIndex > -1) {
        let updateProductsList = [...products];
        updateProductsList[findProductIndex] = parseClientbookProduct({
          id: selectedAddProduct.id,
          image: selectedAddProduct.image,
          name: selectedAddProduct.name,
          qty: 1,
          ...selectedAddProduct,
        });
        setProducts(updateProductsList);
      } else {
        let updateProductsList = [
          parseClientbookProduct({
            id: selectedAddProduct.id,
            image: selectedAddProduct.image,
            name: selectedAddProduct.name,
            qty: 1,
          }),
          ...products,
        ];
        setProducts(updateProductsList);
      }
      setSelectedProductId(selectedAddProduct.id);
    }
  }, [selectedAddProduct]);

  return (
    <div className="flex-row w-100-P h-100-P">
      <ProductsV2List
        title={title}
        productsLoading={productsLoading}
        products={products}
        productsSearch={productsSearch}
        setProductsSearch={setProductsSearch}
        searchField={searchField}
        setSearchField={setSearchField}
        productsPage={productsPage}
        productsTotal={productsTotal}
        setProductsPage={setProductsPage}
        selectedProductId={selectedProductId}
        setSelectedProductId={setSelectedProductId}
        isVendorSelected={isVendorSelected}
        showBrandDropdown={showBrandDropdown}
        setShowBrandDropdown={setShowBrandDropdown}
        selectedBrands={selectedBrands}
        setSelectedBrands={setSelectedBrands}
        selectedCategories={selectedCategories}
        setSelectedCategories={setSelectedCategories}
        vendorId={isVendorSelected ? selectedFilter : null}
        vendorDetails={vendorDetails}
        hasVendorFilters={hasVendorFilters}
        vendorFilters={vendorFilters}
        setVendorFilters={setVendorFilters}
        style={listStyle}
        isClientPage={isClientPage}
        selectedProducts={selectedProducts}
        toggleProductSelection={toggleProductSelection}
        selectedAndDisabledProducts={selectedAndDisabledProducts}
      />

      {isVendorRolex && flags.rolex ? (
        <RolexProductDetails
          loading={productLoading}
          product={selectedProduct}
          onOpenAddInquiry={() => setShowAddInquiryModal(true)}
          inquiryRefreshKey={inquiryRefreshKey}
        />
      ) : (
        <ProductsV2Details
          loading={productLoading}
          product={selectedProduct}
          isVendorSelected={isVendorSelected}
          showClientCollectionModal={showClientCollectionModal}
          setShowClientCollectionModal={setShowClientCollectionModal}
          showShareProductModal={showShareProductModal}
          setShowShareProductModal={setShowShareProductModal}
          showDeleteProductModal={showDeleteProductModal}
          setShowDeleteProductModal={setShowDeleteProductModal}
          showDetailsDrawer={showDetailsDrawer}
          setShowDetailsDrawer={setShowDetailsDrawer}
          fetchProduct={fetchProduct}
          handleOnSuccessSave={handleOnSuccessSave}
          isClientPage={isClientPage}
          getImageUrl={url => setSelectedImageUrl(url)}
        />
      )}
      <ProductsV2Drawer
        open={showDetailsDrawer}
        onClose={() => setShowDetailsDrawer(false)}
        product={selectedProduct}
        zIndex={500}
        style={{
          marginTop: '57px',
          maxHeight: 'calc(100vh - 57px)',
        }}
        hideCost={props.auth.merchantConfig.HIDE_PRODUCT_COST}
      />
      <ClientCollectionModal
        productId={selectedProductId}
        visible={showClientCollectionModal}
        setVisible={setShowClientCollectionModal}
        vendorId={isVendorSelected ? selectedFilter : null}
      />

      <SendProductModal
        productId={selectedProductId}
        visible={showShareProductModal}
        setVisible={setShowShareProductModal}
        vendorId={isVendorSelected ? selectedFilter : null}
        vendorImageUrl={selectedImageUrl || null}
      />

      <DeleteProductModal
        productId={selectedProductId}
        open={showDeleteProductModal}
        setOpen={setShowDeleteProductModal}
        onSuccess={handleDeleteProduct}
      />

      {isVendorRolex && (
        <AddRolexInquiryModal
          open={showAddInquiryModal}
          onCancel={() => setShowAddInquiryModal(false)}
          onSave={() => {
            setShowAddInquiryModal(false);
            setInquiryRefreshKey(k => k + 1);
          }}
          initialModels={inquiryInitialModels}
          vendorId={selectedFilter}
        />
      )}
    </div>
  );
};

const mapStateToProps = state => ({
  auth: state.auth,
});

export default connect(mapStateToProps)(ProductContainer);
